import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import ErrorLogger from '../utils/errorLogger';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import RirHistoryWidget from '../components/RirHistoryWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Camera, Scale, Ruler, TrendingDown, TrendingUp, Upload, Trash2,
  Loader2, ChevronLeft, ChevronRight, Calendar, ArrowRight, Share2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import { GLOW_FILTER, glowTooltipStyle, glowTooltipItemStyle, glowTooltipLabelStyle, themeRgb } from '../components/charts/GlowChartDefs';
import RecordCelebration from '../components/RecordCelebration';
import { StoryGenerator } from '../components/StoryGenerator';
import SelfTimerCamera from '../components/SelfTimerCamera';
import PhotoScanGuideModal from '../components/PhotoScanGuideModal';
import PostureVisionFallbackDialog from '../components/PostureVisionFallbackDialog';
import PhotoCardActionButton from '../components/PhotoCardActionButton';
import J7CelebrationModal, { hasSeenJ7Celebration } from '../components/J7CelebrationModal';
import { prewarmPostureVision } from '../utils/prewarm';
import useCameraViewport from '../utils/useCameraViewport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============ PHOTO VAULT ============
function PhotoVault({ language, onBadgeEarned }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState('face');
  const [compareMode, setCompareMode] = useState(false);
  const [sliderPos, setSliderPos] = useState(50);
  const [comparePhotos, setComparePhotos] = useState({ before: null, after: null });
  const [viewPhoto, setViewPhoto] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  // BlazePose failed → opt-in Gemini Vision fallback dialog
  const [visionFallbackPhotoId, setVisionFallbackPhotoId] = useState(null);
  // Gallery-import historical date dialog
  const [pendingGalleryFile, setPendingGalleryFile] = useState(null);
  const todayISO = new Date().toISOString().slice(0, 10);
  const [historicalDate, setHistoricalDate] = useState(todayISO);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const sliderRef = useRef(null);

  // Iter. 406.d — iOS-safe full-viewport shell for photo viewer modal
  const { containerStyle: photoViewerStyle } = useCameraViewport({ open: !!viewPhoto });

  // Iter. 406.h — J7 celebration trigger. Lazy-loaded check on hasSeen so the
  // import doesn't crash if localStorage is unavailable.
  const [j7CelebrationOpen, setJ7CelebrationOpen] = useState(false);

  const maybeFireJ7Celebration = useCallback((nextPhotos) => {
    try {
      const userId = user?.user_id || user?.id;
      if (!userId) return;
      const seen = hasSeenJ7Celebration(userId);
      if (seen) return;
      const types = new Set((nextPhotos || []).map((p) => (p.photo_type || '').toLowerCase()));
      const hasAllThree = ['face', 'profile', 'dos'].every((k) => types.has(k));
      if (hasAllThree) {
        // Defer one tick so the toast.success above shows first.
        setTimeout(() => setJ7CelebrationOpen(true), 350);
      }
    } catch {
      /* never let celebration logic block the upload happy-path */
    }
  }, [user]);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await api.get('/evolution/photos');
      const list = res.data.photos || [];
      setPhotos(list);
      maybeFireJ7Celebration(list);
    } catch (err) {
      console.error('Error fetching photos:', err);
    } finally {
      setLoading(false);
    }
  }, [maybeFireJ7Celebration]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  // 🧠 Iter. 405.e — fire-and-forget Gemini 2.5 Flash warmup so the
  // optional BlazePose → Vision fallback feels instant. Throttled to
  // once per 30 s session-side + once per 45 s worker-side.
  useEffect(() => { prewarmPostureVision(); }, []);

  // Accept either a DOM event (from <input type="file">) OR a File directly
  // (from the custom in-app SelfTimerCamera). Optional second arg `meta`
  // carries clinical metadata (pose analysis + AI alignment score) OR
  // a historical capture date (for gallery imports of "Avant YUMIX" photos).
  const handleUpload = async (e, meta) => {
    const file = e?.target?.files ? e.target.files[0] : (e instanceof File ? e : null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('evolution.format_non_supporte', 'Format non supporte'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('evolution.fichier_trop_volumineux_max_10_mo', 'Fichier trop volumineux (max 10 Mo)'));
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('photo_type', selectedType);
      // Clinical posture metadata (silent — only useful to Coach in admin dashboard)
      if (meta?.posture) {
        formData.append('posture_analysis', JSON.stringify(meta.posture));
      }
      if (meta?.alignment_score != null) {
        formData.append('alignment_score', String(meta.alignment_score));
      }
      // Historical date for "Avant YUMIX" gallery imports (YYYY-MM-DD).
      // When provided, the 14-day lock is bypassed and the photo is stored with this date.
      if (meta?.captured_at) {
        formData.append('captured_at', meta.captured_at);
      }
      const res = await api.post('/evolution/photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('evolution.photo_sauvegardee', 'Photo sauvegardee !'));
      fetchPhotos();
      // BlazePose fallback trigger — if the client-side pose detection
      // could not produce a usable analysis (no landmarks OR severity
      // explicitly "unavailable"), offer the user the opt-in Gemini Vision
      // fallback. We deliberately skip this for historical/gallery imports
      // (often old photos with unusable framing) to avoid prompt fatigue.
      try {
        const isLiveCapture = !meta?.captured_at;
        const blazeposeOk = !!meta?.posture
          && meta.posture.severity !== 'unavailable'
          && (
            meta.posture.shoulder_height_diff != null
            || meta.posture.pelvis_tilt != null
            || meta.posture.head_tilt != null
            || meta.posture.hip_knee_valgus != null
          );
        const newPhotoId = res?.data?.photo?.photo_id;
        if (isLiveCapture && !blazeposeOk && newPhotoId) {
          setVisionFallbackPhotoId(newPhotoId);
        }
      } catch (_e) {
        // Never let fallback detection break the happy path
      }
      // Check if metamorphose badge earned
      if (res.data.badge_earned) {
        onBadgeEarned(res.data.badge_earned);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur';
      toast.error(detail);
      // Don't log the 429 "too early" lock as a bug — that's expected behavior
      const status = err?.response?.status;
      if (status !== 429 && status !== 400) {
        ErrorLogger.log(err, 'Evolution_Photo_Upload', {
          status,
          photo_type: selectedType,
          has_meta: !!meta,
          is_historical: !!meta?.captured_at,
        });
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
      if (cameraRef.current) cameraRef.current.value = '';
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm(t('evolution.supprimer_cette_photo', 'Supprimer cette photo ?'))) return;
    try {
      await api.delete(`/evolution/photos/${photoId}`);
      toast.success(t('evolution.photo_supprimee', 'Photo supprimee'));
      fetchPhotos();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // Group photos by month
  const grouped = photos.reduce((acc, p) => {
    const key = p.month_label || 'Unknown';
    if (!acc[key]) acc[key] = { face: [], profile: [], dos: [] };
    acc[key][p.photo_type]?.push(p);
    return acc;
  }, {});
  const months = Object.keys(grouped);

  const startCompare = (type) => {
    const typedPhotos = photos.filter(p => p.photo_type === type);
    if (typedPhotos.length < 2) {
      toast.info(t('evolution.il_faut_au_moins_2_photos_du_meme_type_p', 'Il faut au moins 2 photos du même type pour comparer'));
      return;
    }
    setComparePhotos({ before: typedPhotos[0], after: typedPhotos[typedPhotos.length - 1] });
    setSliderPos(50);
    setCompareMode(true);
  };

  const handleSliderMove = (e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const pct = Math.min(100, Math.max(0, (x / rect.width) * 100));
    setSliderPos(pct);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  // Compare view
  if (compareMode && comparePhotos.before && comparePhotos.after) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-black uppercase tracking-tight">
            {t('evolution.comparaison_avant_apres', 'Comparaison Avant / Après')}
          </h3>
          <Button variant="outline" size="sm" onClick={() => setCompareMode(false)} data-testid="close-compare-btn">
            {t('evolution.fermer', 'Fermer')}
          </Button>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <span>{comparePhotos.before.month_label}</span>
          <span>{comparePhotos.after.month_label}</span>
        </div>

        {/* Comparison Slider */}
        <div
          ref={sliderRef}
          className="relative w-full aspect-[3/4] max-w-md mx-auto rounded-xl overflow-hidden cursor-col-resize select-none border border-border"
          onMouseMove={(e) => e.buttons === 1 && handleSliderMove(e)}
          onTouchMove={handleSliderMove}
          data-testid="comparison-slider"
        >
          {/* After (full) */}
          <img
            src={`${BACKEND_URL}${comparePhotos.after.url}`}
            alt="After"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Before (clipped) */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${sliderPos}%` }}
          >
            <img
              src={`${BACKEND_URL}${comparePhotos.before.url}`}
              alt="Before"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ width: `${(100 / sliderPos) * 100}%`, maxWidth: 'none' }}
            />
          </div>
          {/* Divider line */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10"
            style={{ left: `${sliderPos}%` }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
              <ChevronLeft className="w-3 h-3 text-gray-800" />
              <ChevronRight className="w-3 h-3 text-gray-800" />
            </div>
          </div>
          {/* Labels */}
          <div className="absolute top-3 left-3 bg-black/60 text-white text-[10px] font-bold px-2 py-1 rounded">AVANT</div>
          <div className="absolute top-3 right-3 bg-black/60 text-white text-[10px] font-bold px-2 py-1 rounded">APRES</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Upload Section */}
      <Card className="bg-card border-border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" />
            <h3 className="font-bold text-sm">{t('evolution.televersez_votre_photo', 'Televersez votre photo')}</h3>
          </div>
          <div className="flex gap-2">
            {['face', 'profile', 'dos'].map(type => (
              <Button
                key={type}
                variant={selectedType === type ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedType(type)}
                data-testid={`photo-type-${type}`}
              >
                {type === 'face' ? (t('evolution.face', 'Face')) : type === 'profile' ? (t('evolution.profil', 'Profil')) : (t('evolution.dos', 'Dos'))}
              </Button>
            ))}
          </div>
          <Button
            onClick={() => {
              // Show the clinical pre-scan guide first. SelfTimerCamera opens
              // only once the user taps "Démarrer le scan".
              if (navigator.mediaDevices?.getUserMedia) {
                setGuideOpen(true);
              } else {
                cameraRef.current?.click();
              }
            }}
            disabled={uploading}
            className="w-full"
            data-testid="upload-photo-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
            {uploading
              ? (t('evolution.envoi', 'Envoi...'))
              : (t('evolution.prendre_la_photo', 'Prendre la photo'))
            }
          </Button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-full text-[11px] text-muted-foreground hover:text-primary transition-colors inline-flex items-center justify-center gap-1.5 py-1"
            data-testid="upload-photo-gallery-btn"
          >
            <Upload className="w-3 h-3" />
            {t('evolution.ou_choisir_depuis_la_galerie', 'Ou choisir depuis la galerie')}
          </button>
          <p className="text-[11px] text-muted-foreground text-center">
            {t('evolution.une_photo_par_type_tous_les_14_jours', 'Une photo par type tous les 14 jours')}
          </p>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleUpload} data-testid="photo-camera-input" />
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              // Gallery import: stage the file + open date dialog (historical "Avant YUMIX" support)
              const f = e.target.files?.[0];
              if (!f) return;
              if (!f.type.startsWith('image/')) {
                toast.error(t('evolution.format_non_supporte', 'Format non supporte'));
                e.target.value = '';
                return;
              }
              setPendingGalleryFile(f);
              setHistoricalDate(todayISO);
              // reset input value so the same file can be re-picked if user cancels
              e.target.value = '';
            }}
            data-testid="photo-file-input"
          />
        </CardContent>
      </Card>

      {/* Pre-scan clinical guide (silhouettes + instructions + pagination). */}
      <PhotoScanGuideModal
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        onStart={() => { setGuideOpen(false); setCameraOpen(true); }}
        photoType={selectedType}
        language={language}
      />

      {/* Self-timer in-app camera (custom flow with countdown overlay + ghost alignment) */}
      <SelfTimerCamera
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(file, meta) => handleUpload(file, meta)}
        language={language}
        facingMode="user"
        ghostImageUrl={(() => {
          // Most recent photo of the currently selected angle — used as a
          // semi-transparent overlay so the user can align their new pose
          // exactly on top of their previous one (perfect compare-slider).
          const match = photos
            .filter(p => p.photo_type === selectedType)
            .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
          return match ? `${BACKEND_URL}${match.url}` : null;
        })()}
      />

      {/* Gemini Vision fallback opt-in — only shown after BlazePose fails */}
      <PostureVisionFallbackDialog
        isOpen={!!visionFallbackPhotoId}
        photoId={visionFallbackPhotoId}
        onClose={() => setVisionFallbackPhotoId(null)}
        onSuccess={() => {
          setVisionFallbackPhotoId(null);
          fetchPhotos();
        }}
      />

      {/* Iter. 406.h — J7 celebration modal (Avant YUMIX → Cycle 6 sem.) */}
      <J7CelebrationModal
        open={j7CelebrationOpen}
        onClose={() => setJ7CelebrationOpen(false)}
        userId={user?.user_id || user?.id}
        userName={user?.full_name || user?.email}
        language={language}
        alreadyOnEvolution={true}
      />

      {/* Historical date picker (gallery import — "Avant YUMIX" timeline injection) */}
      {pendingGalleryFile && (
        <div
          className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="gallery-date-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-card border border-border rounded-2xl w-full max-w-sm p-5 space-y-4 shadow-2xl">
            <div>
              <h3 className="font-heading uppercase tracking-wider text-base text-foreground mb-1">
                {t('evolution.date_de_la_photo', 'Date de la photo')}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t('evolution.tu_importes_une_photo_historique_choisis', 'Tu importes une photo historique ? Choisis la date à laquelle elle a été prise — on la placera au bon endroit dans ta timeline \\"Avant YUMIX\\".')}
              </p>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 block">
                {t('evolution.date_de_capture', 'Date de capture')}
              </label>
              <input
                type="date"
                value={historicalDate}
                max={todayISO}
                onChange={(e) => setHistoricalDate(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="gallery-date-input"
              />
              <button
                type="button"
                onClick={() => setHistoricalDate(todayISO)}
                className="mt-1.5 text-[11px] text-primary hover:underline"
                data-testid="gallery-date-today-btn"
              >
                {t('evolution.c_est_aujourd_hui', 'C\'est aujourd\'hui')}
              </button>
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setPendingGalleryFile(null)}
                disabled={uploading}
                data-testid="gallery-date-cancel-btn"
              >
                {t('evolution.annuler', 'Annuler')}
              </Button>
              <Button
                type="button"
                className="flex-1"
                disabled={uploading || !historicalDate}
                onClick={() => {
                  const f = pendingGalleryFile;
                  const dateToUse = historicalDate === todayISO ? null : historicalDate;
                  setPendingGalleryFile(null);
                  handleUpload(f, dateToUse ? { captured_at: dateToUse } : undefined);
                }}
                data-testid="gallery-date-confirm-btn"
              >
                {uploading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : (t('evolution.importer', 'Importer'))}
              </Button>
            </div>
          </div>
        </div>
      )}



      {/* Compare Buttons */}
      {photos.length >= 2 && (
        <div className="flex gap-2 w-full min-w-0">
          <Button variant="outline" size="sm" className="flex-1 min-w-0 truncate" onClick={() => startCompare('face')} data-testid="compare-face-btn">
            {t('evolution.comparer_face', 'Comparer Face')}
          </Button>
          <Button variant="outline" size="sm" className="flex-1 min-w-0 truncate" onClick={() => startCompare('profile')} data-testid="compare-profile-btn">
            {t('evolution.comparer_profil', 'Comparer Profil')}
          </Button>
          <Button variant="outline" size="sm" className="flex-1 min-w-0 truncate" onClick={() => startCompare('dos')} data-testid="compare-dos-btn">
            {t('evolution.comparer_dos', 'Comparer Dos')}
          </Button>
        </div>
      )}

      {/* Photo Gallery by Month */}
      {months.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-6 text-center text-muted-foreground">
            <Camera className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">{t('evolution.aucune_photo_pour_le_moment', 'Aucune photo pour le moment')}</p>
            <p className="text-xs mt-1">{t('evolution.commencez_a_documenter_votre_transformat', 'Commencez a documenter votre transformation !')}</p>
          </CardContent>
        </Card>
      ) : (
        months.map(month => (
          <Card key={month} className="bg-card border-border">
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-sm font-bold flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                {month}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="grid grid-cols-3 gap-3">
                {['face', 'profile', 'dos'].map(type => {
                  const typePhotos = grouped[month]?.[type] || [];
                  return typePhotos.map(photo => (
                    <div key={photo.photo_id} className="relative group rounded-lg overflow-hidden border border-border aspect-[3/4] cursor-pointer" onClick={() => setViewPhoto(photo)} data-testid={`photo-container-${photo.photo_id}`}>
                      <img
                        src={`${BACKEND_URL}${photo.url}`}
                        alt={`${type} - ${month}`}
                        className="w-full h-full object-cover"
                        data-testid={`photo-${photo.photo_id}`}
                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-muted', 'flex', 'items-center', 'justify-center'); }}
                      />
                      <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase z-10">
                        {type === 'face' ? 'Face' : type === 'profile' ? 'Profil' : 'Dos'}
                      </div>
                      {/* Iter. 406.h — reusable mobile-first delete chip */}
                      <PhotoCardActionButton
                        icon={Trash2}
                        tone="danger"
                        onClick={() => handleDelete(photo.photo_id)}
                        ariaLabel="Supprimer la photo"
                        testId={`delete-photo-${photo.photo_id}`}
                      />
                    </div>
                  ));
                })}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Fullscreen Photo Viewer */}
      {viewPhoto && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          style={photoViewerStyle}
          onClick={() => setViewPhoto(null)}
          data-testid="photo-viewer-overlay"
        >
          <button 
            className="absolute top-4 right-4 text-white bg-white/20 rounded-full p-2 z-10"
            style={{ top: 'max(env(safe-area-inset-top, 0px), 1rem)' }}
            onClick={() => setViewPhoto(null)}
            data-testid="photo-viewer-close"
          >
            <span className="text-lg font-bold">&#x2715;</span>
          </button>
          <div
            className="absolute top-4 left-4 bg-black/60 text-white text-xs font-bold px-3 py-1.5 rounded uppercase"
            style={{ top: 'max(env(safe-area-inset-top, 0px), 1rem)' }}
          >
            {viewPhoto.photo_type === 'face' ? 'Face' : viewPhoto.photo_type === 'profile' ? 'Profil' : 'Dos'}
            {viewPhoto.month_label && ` — ${viewPhoto.month_label}`}
          </div>
          <img
            src={`${BACKEND_URL}${viewPhoto.url}`}
            alt={viewPhoto.photo_type}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="photo-viewer-image"
          />
        </div>
      )}
    </div>
  );
}


// ============ WEIGHT LOG ============
// Helper: format YYYY-MM-DD in local time (avoids UTC drift on toISOString)
const formatLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const TODAY_YMD = () => formatLocalYMD(new Date());
const YMD_DAYS_AGO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return formatLocalYMD(d);
};

function WeightLog({ language, onCelebrations }) {
  const { t } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weight, setWeight] = useState('');
  const [saving, setSaving] = useState(false);
  const [prefilledFromAI, setPrefilledFromAI] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const weightInputRef = useRef(null);
  // Retroactive logging — Phase 1 (2026-04-29). Default to today; users can
  // pick any date in the last 30 days to backfill missed entries.
  const [logDate, setLogDate] = useState(TODAY_YMD());
  const minDate = YMD_DAYS_AGO(30);
  const todayStr = TODAY_YMD();

  // Pre-fill from Assistant IA deep-link (e.g. /evolution?weight=185)
  useEffect(() => {
    const w = searchParams.get('weight');
    if (w) {
      const n = parseFloat(w);
      if (!isNaN(n) && n > 0 && n < 800) {
        setWeight(String(n));
        setPrefilledFromAI(true);
        // Scroll the input into view + focus it after a short delay
        setTimeout(() => {
          if (weightInputRef.current) {
            weightInputRef.current.focus();
            weightInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 400);
        // Clear the query param so a page refresh does not re-prefill
        const next = new URLSearchParams(searchParams);
        next.delete('weight');
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWeight = useCallback(async () => {
    try {
      const res = await api.get('/evolution/weight');
      setEntries(res.data.entries || []);
      setTrend(res.data.trend || []);
    } catch (err) {
      console.error('Error fetching weight:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWeight(); }, [fetchWeight]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const val = parseFloat(weight);
    if (!val || val <= 0) {
      toast.error(t('evolution.err_invalid_weight', 'Entrez un poids valide'));
      return;
    }
    const isRetro = logDate !== todayStr;
    setSaving(true);
    try {
      const payload = { weight: val, unit: 'lbs' };
      if (isRetro) payload.target_date = logDate;
      const res = await api.post('/evolution/weight', payload);
      toast.success(
        isRetro
          ? t('evolution.weight_logged_for_date', 'Poids enregistré pour le {{date}}', { date: logDate })
          : t('evolution.weight_logged', 'Poids enregistré !')
      );
      setWeight('');
      setLogDate(todayStr); // reset to today after a retroactive save
      fetchWeight();
      // Trigger celebrations if any (only fired by backend on today entries)
      if (res.data.celebrations && res.data.celebrations.length > 0) {
        onCelebrations(res.data.celebrations);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const totalChange = entries.length >= 2
    ? (entries[entries.length - 1].weight - entries[0].weight).toFixed(1)
    : null;

  return (
    <div className="space-y-4">
      {/* Weight Input */}
      <Card className={`bg-card border-border ${prefilledFromAI ? 'border-primary ring-2 ring-primary/40 animate-pulse-once' : ''}`}>
        <CardContent className="p-4">
          {prefilledFromAI && (
            <p className="text-[11px] font-bold text-primary uppercase tracking-wider mb-2" data-testid="weight-prefilled-banner">
              {t('evolution.ai_prefilled_banner', 'Assistant IA : {{weight}} lbs pré-rempli. Confirme pour enregistrer.', { weight })}
            </p>
          )}

          {/* Retroactive date picker — Phase 1 (2026-04-29). Defaults to today.
              The orange "rattrapage" banner makes it obvious the user is
              about to backfill a past day. */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                <Calendar className="w-3 h-3" />
                {t('evolution.weight_date_label', 'Date du poids')}
              </label>
              {logDate !== todayStr && (
                <button
                  type="button"
                  onClick={() => setLogDate(todayStr)}
                  className="text-[10px] uppercase tracking-widest text-primary hover:text-primary/80 font-bold"
                  data-testid="weight-reset-today"
                >
                  {t('evolution.today_btn', "Aujourd'hui")}
                </button>
              )}
            </div>
            <Input
              type="date"
              value={logDate}
              min={minDate}
              max={todayStr}
              onChange={(e) => setLogDate(e.target.value || todayStr)}
              className="h-9 text-sm"
              data-testid="weight-date-input"
            />
            {logDate !== todayStr && (
              <p
                className="mt-1.5 text-[11px] text-orange-400 bg-orange-500/10 border border-orange-500/30 rounded-md px-2 py-1.5 leading-snug"
                data-testid="weight-retro-banner"
              >
                {t('evolution.backfill_banner', 'Rattrapage · tu enregistres pour le {{date}}.', { date: logDate })}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-xs font-bold text-muted-foreground mb-1 block">
                {t('evolution.weight_lbs_label', 'Poids (lbs)')}
              </label>
              <Input
                ref={weightInputRef}
                type="number"
                step="0.1"
                value={weight}
                onChange={e => { setWeight(e.target.value); setPrefilledFromAI(false); }}
                placeholder={t('evolution.weight_placeholder', 'Ex: 185.5')}
                data-testid="weight-input"
              />
            </div>
            <Button type="submit" disabled={saving} data-testid="log-weight-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4 mr-1" />}
              {prefilledFromAI
                ? t('evolution.confirm_btn', 'Confirmer')
                : t('evolution.log_btn', 'Loguer')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Summary Card */}
      {totalChange !== null && (
        <Card className={`border ${parseFloat(totalChange) <= 0 ? 'border-green-500/30 bg-green-500/5' : 'border-orange-500/30 bg-orange-500/5'}`}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">{t('evolution.evolution_totale', 'Évolution totale')}</p>
              <p className={`text-2xl font-black ${parseFloat(totalChange) <= 0 ? 'text-green-400' : 'text-orange-400'}`}>
                {parseFloat(totalChange) > 0 ? '+' : ''}{totalChange} lbs
              </p>
            </div>
            {parseFloat(totalChange) <= 0
              ? <TrendingDown className="w-8 h-8 text-green-400" />
              : <TrendingUp className="w-8 h-8 text-orange-400" />
            }
          </CardContent>
        </Card>
      )}

      {/* Trend Chart */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : trend.length >= 2 ? (
        <Card className="bg-card border-border">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-primary" />
              {t('evolution.courbe_de_tendance', 'Courbe de tendance')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="weightStroke" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#6ee7b7" stopOpacity={1} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="weightGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#34d399" stopOpacity={0.45} />
                    <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={`rgba(${themeRgb('emerald')},0.12)`} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(d) => {
                    const parts = d.split('-');
                    return `${parts[1]}/${parts[2]}`;
                  }}
                />
                <YAxis
                  domain={['dataMin - 2', 'dataMax + 2']}
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                />
                <Tooltip
                  cursor={{ stroke: `rgba(${themeRgb('emerald')},0.30)`, strokeWidth: 1 }}
                  contentStyle={glowTooltipStyle(themeRgb('emerald'))}
                  itemStyle={glowTooltipItemStyle()}
                  labelStyle={glowTooltipLabelStyle(themeRgb('emerald'))}
                  formatter={(value, name) => [
                    `${value} lbs`,
                    name === 'trend' ? (t('evolution.tendance', 'Tendance')) : (t('evolution.poids', 'Poids'))
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="weight"
                  stroke="url(#weightStroke)"
                  fill="url(#weightGrad)"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#34d399' }}
                  activeDot={{ r: 5, fill: '#6ee7b7', stroke: '#10b981', strokeWidth: 2, style: { filter: GLOW_FILTER(themeRgb('emerald')) } }}
                  style={{ filter: GLOW_FILTER(themeRgb('emerald')) }}
                />
                <Line type="monotone" dataKey="trend" stroke="#fb923c" strokeWidth={2} strokeDasharray="5 5" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="p-6 text-center text-muted-foreground">
            <Scale className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">{t('evolution.loguez_au_moins_2_pesees_pour_voir_la_co', 'Loguez au moins 2 pesees pour voir la courbe')}</p>
          </CardContent>
        </Card>
      )}

      {/* Recent Entries */}
      {entries.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-bold">{t('evolution.historique', 'Historique')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {[...entries].reverse().slice(0, 20).map((e, i) => (
                <div key={i} className="flex justify-between items-center text-sm py-1.5 border-b border-border/50 last:border-0">
                  <span className="text-muted-foreground text-xs">{e.date}</span>
                  <span className="font-bold">{e.weight} lbs</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ============ MEASUREMENTS LOG ============
function MeasurementsLog({ language }) {
  const { t } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ waist: '', chest: '', arms: '', thighs: '' });
  const [aiPrefilled, setAiPrefilled] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Pre-fill from Assistant IA deep-link
  useEffect(() => {
    const keys = ['waist', 'chest', 'arms', 'thighs'];
    const patch = {};
    let has = false;
    keys.forEach(k => {
      const v = searchParams.get(k);
      if (v) {
        const n = parseFloat(v);
        if (!isNaN(n) && n > 0 && n < 200) { patch[k] = String(n); has = true; }
      }
    });
    if (has) {
      setForm(prev => ({ ...prev, ...patch }));
      setAiPrefilled(true);
      const next = new URLSearchParams(searchParams);
      keys.forEach(k => next.delete(k));
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMeasurements = useCallback(async () => {
    try {
      const res = await api.get('/evolution/measurements');
      setEntries(res.data.entries || []);
    } catch (err) {
      console.error('Error fetching measurements:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMeasurements(); }, [fetchMeasurements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {};
    if (form.waist) data.waist = parseFloat(form.waist);
    if (form.chest) data.chest = parseFloat(form.chest);
    if (form.arms) data.arms = parseFloat(form.arms);
    if (form.thighs) data.thighs = parseFloat(form.thighs);

    if (Object.keys(data).length === 0) {
      toast.error(t('evolution.entrez_au_moins_une_mensuration', 'Entrez au moins une mensuration'));
      return;
    }

    setSaving(true);
    try {
      await api.post('/évolution/measurements', { ...data, unit: 'in' });
      toast.success(t('evolution.mensurations_enregistrees', 'Mensurations enregistrees !'));
      setForm({ waist: '', chest: '', arms: '', thighs: '' });
      fetchMeasurements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'waist', label_fr: 'Taille (po)', label_en: 'Waist (in)' },
    { key: 'chest', label_fr: 'Poitrine (po)', label_en: 'Chest (in)' },
    { key: 'arms', label_fr: 'Bras (po)', label_en: 'Arms (in)' },
    { key: 'thighs', label_fr: 'Cuisses (po)', label_en: 'Thighs (in)' },
  ];

  return (
    <div className="space-y-4">
      {/* Input Form */}
      <Card className={`bg-card border-border ${aiPrefilled ? 'border-primary ring-2 ring-primary/40' : ''}`}>
        <CardContent className="p-4">
          {aiPrefilled && (
            <p className="text-[11px] font-bold text-primary uppercase tracking-wider mb-2" data-testid="measurements-prefilled-banner">
              {t('evolution.assistant_ia_mensurations_pre_remplies_c', 'Assistant IA : mensurations pré-remplies. Confirme pour enregistrer.')}
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {fields.map(f => (
                <div key={f.key}>
                  <label className="text-[11px] font-bold text-muted-foreground mb-1 block">
                    {t(`evolution.field_${f.key}`, f.label_fr)}
                  </label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form[f.key]}
                    onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder="--"
                    data-testid={`measurement-${f.key}`}
                  />
                </div>
              ))}
            </div>
            <Button type="submit" className="w-full" disabled={saving} data-testid="log-measurements-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ruler className="w-4 h-4 mr-2" />}
              {t('evolution.enregistrer', 'Enregistrer')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Measurements Chart */}
      {!loading && entries.length >= 2 && (
        <Card className="bg-card border-border">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Ruler className="w-4 h-4 text-primary" />
              {t('evolution.evolution_des_mensurations', 'Évolution des mensurations')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={entries}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(168,85,247,0.10)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(d) => {
                    const parts = d.split('-');
                    return `${parts[1]}/${parts[2]}`;
                  }}
                />
                <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                <Tooltip
                  cursor={{ stroke: 'rgba(168,85,247,0.30)', strokeWidth: 1 }}
                  contentStyle={glowTooltipStyle('168,85,247')}
                  itemStyle={glowTooltipItemStyle()}
                  labelStyle={glowTooltipLabelStyle('168,85,247')}
                />
                <Line type="monotone" dataKey="waist"  stroke="#f59e0b" strokeWidth={2.5} name={t('evolution.taille', 'Taille')}      connectNulls style={{ filter: GLOW_FILTER('245,158,11') }} />
                <Line type="monotone" dataKey="chest"  stroke="#3b82f6" strokeWidth={2.5} name={t('evolution.poitrine', 'Poitrine')}  connectNulls style={{ filter: GLOW_FILTER('59,130,246') }} />
                <Line type="monotone" dataKey="arms"   stroke="#10b981" strokeWidth={2.5} name={t('evolution.bras', 'Bras')}          connectNulls style={{ filter: GLOW_FILTER('16,185,129') }} />
                <Line type="monotone" dataKey="thighs" stroke="#ef4444" strokeWidth={2.5} name={t('evolution.cuisses', 'Cuisses')}    connectNulls style={{ filter: GLOW_FILTER('239,68,68') }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Recent Entries */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : entries.length > 0 ? (
        <Card className="bg-card border-border">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-bold">{t('evolution.historique', 'Historique')}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {[...entries].reverse().slice(0, 10).map((e, i) => (
                <div key={i} className="text-xs py-2 border-b border-border/50 last:border-0">
                  <p className="text-muted-foreground mb-1">{e.date}</p>
                  <div className="flex flex-wrap gap-3">
                    {e.waist && <span><strong className="text-amber-400">{t('evolution.taille', 'Taille')}:</strong> {e.waist}"</span>}
                    {e.chest && <span><strong className="text-blue-400">{t('evolution.poitrine', 'Poitrine')}:</strong> {e.chest}"</span>}
                    {e.arms && <span><strong className="text-green-400">{t('evolution.bras', 'Bras')}:</strong> {e.arms}"</span>}
                    {e.thighs && <span><strong className="text-red-400">{t('evolution.cuisses', 'Cuisses')}:</strong> {e.thighs}"</span>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="p-6 text-center text-muted-foreground">
            <Ruler className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">{t('evolution.aucune_mensuration_enregistree', 'Aucune mensuration enregistrée')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ============ MAIN PAGE ============
export default function Evolution() {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const [activeCelebration, setActiveCelebration] = useState(null);
  const [badgeCelebration, setBadgeCelebration] = useState(null);

  // Default tab = based on Assistant IA deep-link params
  const initialTab = searchParams.get('tab')
    || (searchParams.get('weight') ? 'weight'
        : (searchParams.get('waist') || searchParams.get('chest') || searchParams.get('arms') || searchParams.get('thighs')) ? 'measurements'
        : 'photos');

  // Check for pending unseen celebrations on mount
  useEffect(() => {
    api.get('/evolution/celebrations')
      .then(res => {
        const pending = res.data.celebrations || [];
        if (pending.length > 0) {
          setCelebrationQueue(pending);
          setActiveCelebration(pending[0]);
        }
      })
      .catch(() => {});
  }, []);

  const handleNewCelebrations = (newCelebs) => {
    if (newCelebs.length > 0) {
      setCelebrationQueue(prev => [...prev, ...newCelebs]);
      if (!activeCelebration) {
        setActiveCelebration(newCelebs[0]);
      }
    }
  };

  const handleDismissCelebration = () => {
    if (activeCelebration) {
      // Mark as seen
      api.patch('/evolution/celebrations/seen', {
        celebration_ids: [activeCelebration.celebration_id]
      }).catch(() => {});
    }
    // Show next in queue
    const remaining = celebrationQueue.filter(
      c => c.celebration_id !== activeCelebration?.celebration_id
    );
    setCelebrationQueue(remaining);
    setActiveCelebration(remaining.length > 0 ? remaining[0] : null);
  };

  const handleBadgeEarned = (badge) => {
    const celeb = {
      celebration_id: `badge-${badge.badge_id}`,
      type: 'personal_record',
      title: `Badge "${badge.name}" debloque !`,
      message: badge.description + ' Tu es un vrai champion de la transformation. La communaute te salue !',
    };
    setCelebrationQueue(prev => [...prev, celeb]);
    if (!activeCelebration) {
      setActiveCelebration(celeb);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6" data-testid="evolution-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-foreground">
            {t('evolution.mon_evolution', 'Mon Évolution')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('evolution.documentez_votre_metamorphose_la_preuve_', 'Documentez votre metamorphose — la preuve est dans le miroir.')}
          </p>
        </div>

        {/* RIR Intensity histogram — 4-week trend */}
        <RirHistoryWidget language={language} />

        {/* Tabs */}
        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-4">
            <TabsTrigger value="photos" data-testid="tab-photos">
              <Camera className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">{t('evolution.voute_photo', 'Voute Photo')}</span>
              <span className="sm:hidden">{t('evolution.photos', 'Photos')}</span>
            </TabsTrigger>
            <TabsTrigger value="weight" data-testid="tab-weight">
              <Scale className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">{t('evolution.poids', 'Poids')}</span>
              <span className="sm:hidden">{t('evolution.poids', 'Poids')}</span>
            </TabsTrigger>
            <TabsTrigger value="measurements" data-testid="tab-measurements">
              <Ruler className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">{t('evolution.mensurations', 'Mensurations')}</span>
              <span className="sm:hidden">{t('evolution.mesures', 'Mesures')}</span>
            </TabsTrigger>
            <TabsTrigger value="story" data-testid="tab-story">
              <Share2 className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Story</span>
              <span className="sm:hidden">Story</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="photos">
            <PhotoVault language={language} onBadgeEarned={handleBadgeEarned} />
          </TabsContent>

          <TabsContent value="weight">
            <WeightLog language={language} onCelebrations={handleNewCelebrations} />
          </TabsContent>

          <TabsContent value="measurements">
            <MeasurementsLog language={language} />
          </TabsContent>

          <TabsContent value="story">
            <StoryGenerator language={language} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Record Celebration Overlay */}
      {activeCelebration && (
        <RecordCelebration
          celebration={activeCelebration}
          onClose={handleDismissCelebration}
          language={language}
        />
      )}
    </Layout>
  );
}
